import React, { useMemo, useState } from "react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ClosedPositionData,
  PositionData,
} from "@crypto-trading/common/browser";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DesktopLayout } from "@/layouts";
import { TabSelector } from "@/components/TabSelector";
import {
  CopyTradeExchangeSelect,
  type CopyTradeExchange,
} from "@/components/copy-trading/CopyTradeExchangeSelect";
import { CopyTradeLeaderCard } from "@/components/copy-trading/CopyTradeLeaderCard";
import { CopyTradeSubscribedTile } from "@/components/copy-trading/CopyTradeSubscribedTile";
import { CopyTradeLeaderDetailsDialog } from "@/components/copy-trading/CopyTradeLeaderDetailsDialog";
import { CopyTradeMirroredPositionsDialog } from "@/components/copy-trading/CopyTradeMirroredPositionsDialog";
import { CopyTradeSubscribeDrawer } from "@/components/copy-trading/CopyTradeSubscribeDrawer";
import { CopyTradeUnsubscribeDialog } from "@/components/copy-trading/CopyTradeUnsubscribeDialog";
import { PositionDetailDrawerDesktop } from "@/pages/positions/drawers";
import type { CopyTradeLeader, CopyTradeSubscription, CopyTradeLeaderSnapshot } from "@/api/copy-trade";
import {
  copyTradeKeys,
  useCopyTradeLeaders,
  useCopyTradeSubscriptions,
  useSubscribeCopyTrade,
  useUnsubscribeCopyTrade,
  useUpdateCopyTradeSubscription,
} from "@/hooks/useCopyTrade";

type ViewTab = "browse" | "subscribed";

export const CopyTradingPageDesktop: React.FC = () => {
  const launchParams = useLaunchParams();
  const queryClient = useQueryClient();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [exchange, setExchange] = useState<CopyTradeExchange>("binance");
  const [viewTab, setViewTab] = useState<ViewTab>("browse");
  const [detailsTarget, setDetailsTarget] = useState<{
    leadPortfolioId: string;
    nickname: string;
    snapshot?: CopyTradeLeaderSnapshot;
  } | null>(null);
  const [positionsTarget, setPositionsTarget] =
    useState<CopyTradeSubscription | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<
    PositionData | ClosedPositionData | null
  >(null);
  const [subscribeLeader, setSubscribeLeader] = useState<CopyTradeLeader | null>(
    null
  );
  const [unsubscribeTarget, setUnsubscribeTarget] =
    useState<CopyTradeSubscription | null>(null);

  const { data: leaders = [], isLoading: leadersLoading } =
    useCopyTradeLeaders("binance");
  const { data: subscriptions = [], isLoading: subsLoading } =
    useCopyTradeSubscriptions(userId);

  const subscribeMutation = useSubscribeCopyTrade();
  const unsubscribeMutation = useUnsubscribeCopyTrade();
  const updateMutation = useUpdateCopyTradeSubscription();

  const subscribedPortfolioIds = useMemo(
    () => new Set(subscriptions.map((s) => s.leadPortfolioId)),
    [subscriptions]
  );

  const browseLeaders = useMemo(
    () =>
      leaders.filter((l) => !subscribedPortfolioIds.has(l.leadPortfolioId)),
    [leaders, subscribedPortfolioIds]
  );

  const handleSubscribe = async (payload: {
    fixedUsdAmount: number;
    takeProfitPercent?: number;
    stopLossPercent?: number;
  }) => {
    if (!subscribeLeader || !userId) return;
    try {
      await subscribeMutation.mutateAsync({
        userId,
        payload: {
          leadPortfolioId: subscribeLeader.leadPortfolioId,
          leadNickname: subscribeLeader.nickname,
          leadAvatarUrl: subscribeLeader.avatarUrl,
          leaderSnapshot: subscribeLeader,
          fixedUsdAmount: payload.fixedUsdAmount,
          takeProfitPercent: payload.takeProfitPercent,
          stopLossPercent: payload.stopLossPercent,
        },
      });
      toast.success(`Subscribed to ${subscribeLeader.nickname}`);
      setSubscribeLeader(null);
      setViewTab("subscribed");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to subscribe"
      );
    }
  };

  return (
    <DesktopLayout
      pageTitle="Copy Trading"
      pageDescription="Mirror Binance lead traders on your connected exchange"
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-secondary">Exchange</span>
          <CopyTradeExchangeSelect value={exchange} onChange={setExchange} />
        </div>

        <TabSelector<ViewTab>
          options={[
            { value: "browse", label: "Browse" },
            { value: "subscribed", label: "Subscribed" },
          ]}
          value={viewTab}
          onChange={setViewTab}
        />

        {viewTab === "browse" && (
          <>
            {leadersLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : browseLeaders.length === 0 ? (
              <div className="text-center py-16 text-text-secondary">
                No new leaders to browse. Check Subscribed tab for your
                portfolios.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {browseLeaders.map((leader) => (
                  <CopyTradeLeaderCard
                    key={leader.leadPortfolioId}
                    leader={leader}
                    isSubscribed={subscribedPortfolioIds.has(
                      leader.leadPortfolioId
                    )}
                    onViewDetails={() =>
                      setDetailsTarget({
                        leadPortfolioId: leader.leadPortfolioId,
                        nickname: leader.nickname,
                        snapshot: leader,
                      })
                    }
                    onSubscribe={() => setSubscribeLeader(leader)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {viewTab === "subscribed" && (
          <>
            {subsLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : subscriptions.length === 0 ? (
              <div className="text-center py-16 text-text-secondary">
                You have no active copy trade subscriptions. Browse leaders to
                get started.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {subscriptions.map((sub) => (
                  <CopyTradeSubscribedTile
                    key={sub.id}
                    subscription={sub}
                    isSaving={updateMutation.isPending}
                    onViewDetails={() =>
                      setDetailsTarget({
                        leadPortfolioId: sub.leadPortfolioId,
                        nickname: sub.leadNickname,
                        snapshot: sub.leaderSnapshot,
                      })
                    }
                    onViewPositions={() => setPositionsTarget(sub)}
                    onUnsubscribe={() => setUnsubscribeTarget(sub)}
                    onSaveSettings={async (payload) => {
                      await updateMutation.mutateAsync({
                        subscriptionId: sub.id,
                        userId,
                        payload,
                      });
                      toast.success("Settings updated");
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <CopyTradeLeaderDetailsDialog
        open={!!detailsTarget}
        onOpenChange={(open) => !open && setDetailsTarget(null)}
        leadPortfolioId={detailsTarget?.leadPortfolioId ?? ""}
        nickname={detailsTarget?.nickname ?? ""}
        snapshot={detailsTarget?.snapshot}
        isSubscribed={
          detailsTarget
            ? subscribedPortfolioIds.has(detailsTarget.leadPortfolioId)
            : false
        }
        onSubscribe={
          detailsTarget
            ? () => {
                const fromBrowse = leaders.find(
                  (l) => l.leadPortfolioId === detailsTarget.leadPortfolioId
                );
                const leader: CopyTradeLeader = fromBrowse ?? {
                  ...detailsTarget.snapshot,
                  leadPortfolioId: detailsTarget.leadPortfolioId,
                  nickname: detailsTarget.nickname,
                };
                setDetailsTarget(null);
                setSubscribeLeader(leader);
              }
            : undefined
        }
      />

      <CopyTradeMirroredPositionsDialog
        open={!!positionsTarget}
        onOpenChange={(open) => !open && setPositionsTarget(null)}
        subscriptionId={positionsTarget?.id ?? ""}
        userId={userId}
        nickname={positionsTarget?.leadNickname ?? ""}
        onSelectPosition={(position) => {
          setPositionsTarget(null);
          setSelectedPosition(position);
        }}
      />

      <PositionDetailDrawerDesktop
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        onPositionUpdate={(updatedPosition) => {
          setSelectedPosition(updatedPosition);
          if (positionsTarget) {
            queryClient.invalidateQueries({
              queryKey: copyTradeKeys.mirroredPositions(
                positionsTarget.id,
                userId
              ),
            });
          }
          queryClient.invalidateQueries({
            queryKey: copyTradeKeys.subscriptions(userId),
          });
        }}
        source="copy_trading"
      />

      <CopyTradeSubscribeDrawer
        open={!!subscribeLeader}
        onOpenChange={(open) => !open && setSubscribeLeader(null)}
        leader={subscribeLeader}
        isSubmitting={subscribeMutation.isPending}
        onSubmit={handleSubscribe}
      />

      <CopyTradeUnsubscribeDialog
        open={!!unsubscribeTarget}
        onOpenChange={(open) => !open && setUnsubscribeTarget(null)}
        nickname={unsubscribeTarget?.leadNickname ?? ""}
        isLoading={unsubscribeMutation.isPending}
        onConfirm={async () => {
          if (!unsubscribeTarget || !userId) return;
          try {
            await unsubscribeMutation.mutateAsync({
              subscriptionId: unsubscribeTarget.id,
              userId,
            });
            toast.success(`Unsubscribed from ${unsubscribeTarget.leadNickname}`);
            setUnsubscribeTarget(null);
          } catch (error) {
            toast.error(
              error instanceof Error ? error.message : "Failed to unsubscribe"
            );
          }
        }}
      />
    </DesktopLayout>
  );
};
